/**
 * AnnouncementBanner — renders active admin announcements for the current user.
 *
 * - Fetches from GET /api/announcements/my on mount + on role change.
 * - Stamps a view (idempotent) the first time each banner is seen.
 * - "Got it" button calls POST /api/announcements/{id}/read and hides locally.
 * - Dismissed + read banners don't render again (backend already filters).
 *
 * Drop into any dashboard at the top of the content region.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { X, Info, CheckCircle2, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function authHeaders() {
  const token = typeof window !== "undefined" ? window.localStorage.getItem("token") : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const SEVERITY_STYLES = {
  info: {
    box: "bg-blue-50 border-blue-200 text-blue-900",
    icon: Info,
    iconClass: "text-blue-600",
    btn: "bg-blue-600 hover:bg-blue-700 text-white",
  },
  success: {
    box: "bg-emerald-50 border-emerald-200 text-emerald-900",
    icon: CheckCircle2,
    iconClass: "text-emerald-600",
    btn: "bg-emerald-600 hover:bg-emerald-700 text-white",
  },
  warning: {
    box: "bg-amber-50 border-amber-300 text-amber-900",
    icon: AlertTriangle,
    iconClass: "text-amber-700",
    btn: "bg-amber-600 hover:bg-amber-700 text-white",
  },
};

export default function AnnouncementBanner({ role }) {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/announcements/my`, { headers: authHeaders() });
        if (cancelled) return;
        const list = (r.data?.announcements || []).filter((a) => !a.read);
        setItems(list);
        // Fire-and-forget view stamps.
        list.forEach((a) => {
          axios.post(`${API}/announcements/${a.id}/view`, {}, { headers: authHeaders() }).catch(() => {});
        });
      } catch {
        // Silent — if announcements fail, don't block the dashboard.
      }
    })();
    return () => { cancelled = true; };
  }, [role]);

  const markRead = async (id) => {
    setDismissed((s) => new Set(s).add(id));
    try {
      await axios.post(`${API}/announcements/${id}/read`, {}, { headers: authHeaders() });
    } catch {
      // Rollback only if the request failed in a way the user would care about.
      // Silent fail is fine — they'll see it again next load if it didn't stick.
    }
  };

  const visible = items.filter((a) => !dismissed.has(a.id));
  if (!visible.length) return null;

  return (
    <div className="space-y-3 mb-5" data-testid="announcement-banner-container">
      {visible.map((a) => {
        const sev = SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.info;
        const Icon = sev.icon;
        return (
          <div
            key={a.id}
            role="region"
            aria-label={`Announcement: ${a.title}`}
            data-testid={`announcement-banner-${a.id}`}
            className={`relative border rounded-xl px-4 py-3 sm:px-5 sm:py-4 shadow-sm ${sev.box}`}
          >
            <button
              type="button"
              onClick={() => markRead(a.id)}
              aria-label="Dismiss announcement"
              title="Dismiss"
              data-testid={`announcement-dismiss-${a.id}`}
              className="absolute top-2 right-2 p-1 rounded hover:bg-black/5"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex gap-3 items-start pr-6">
              <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${sev.iconClass}`} aria-hidden="true" />
              <div className="flex-1">
                <div className="font-bold text-sm sm:text-base">{a.title}</div>
                <div className="text-sm mt-0.5 whitespace-pre-wrap leading-relaxed">{a.body}</div>
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => markRead(a.id)}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold ${sev.btn}`}
                    data-testid={`announcement-got-it-${a.id}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
                    Got it
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
